import type { App, ComponentPublicInstance } from 'vue';

type RuntimeErrorKind = 'bootstrap' | 'unhandledrejection' | 'vue' | 'window';

type RuntimeErrorPayload = {
  kind: RuntimeErrorKind;
  message: string;
  timestamp: string;
  url: string;
  column?: number;
  component?: string;
  info?: string;
  line?: number;
  name?: string;
  source?: string;
  stack?: string;
};

declare global {
  interface Window {
    __OVERPASS_GLOBAL_ERROR_HANDLERS_INSTALLED__?: boolean;
  }
}

const RECENT_ERROR_WINDOW_MS = 5_000;
const MAX_FIELD_LENGTH = 4_000;
const recentErrorKeys = new Map<string, number>();
const seenErrorObjects = new WeakSet<object>();

function clip(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.length > MAX_FIELD_LENGTH ? `${value.slice(0, MAX_FIELD_LENGTH)}...` : value;
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

function normalizeError(error: unknown): Pick<RuntimeErrorPayload, 'message' | 'name' | 'stack'> {
  if (error instanceof Error) {
    return {
      message: clip(error.message) ?? error.name,
      name: clip(error.name),
      stack: clip(error.stack),
    };
  }

  return {
    message: clip(stringifyValue(error)) ?? 'Unknown error',
  };
}

function getComponentName(instance: ComponentPublicInstance | null): string | undefined {
  const type = instance?.$?.type as { __name?: string; name?: string } | undefined;
  return type?.name ?? type?.__name;
}

function buildErrorKey(payload: RuntimeErrorPayload): string {
  return [
    payload.kind,
    payload.message,
    payload.component,
    payload.info,
    payload.source,
    payload.line,
    payload.column,
  ]
    .filter((value) => value !== undefined && value !== '')
    .join('|');
}

function isDuplicateError(error: unknown, payload: RuntimeErrorPayload): boolean {
  const now = Date.now();
  for (const [key, seenAt] of recentErrorKeys) {
    if ((now - seenAt) > RECENT_ERROR_WINDOW_MS) {
      recentErrorKeys.delete(key);
    }
  }

  const key = buildErrorKey(payload);
  const duplicateKey = (now - (recentErrorKeys.get(key) ?? 0)) < RECENT_ERROR_WINDOW_MS;
  recentErrorKeys.set(key, now);

  if (error !== null && typeof error === 'object') {
    if (seenErrorObjects.has(error)) return true;
    seenErrorObjects.add(error);
  }

  return duplicateKey;
}

function emitRuntimeError(error: unknown, payload: RuntimeErrorPayload): void {
  if (isDuplicateError(error, payload)) return;

  console.error('[overpass-planner]', payload);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<RuntimeErrorPayload>('overpass:runtime-error', {
      detail: payload,
    }));
  }
}

function resolvePageUrl(): string {
  return typeof window === 'undefined' ? 'server' : window.location.href;
}

function getResourceSource(target: EventTarget | null): string | undefined {
  if (typeof window === 'undefined' || !target || target === window) return undefined;
  if (!(target instanceof HTMLElement)) return undefined;

  if (target instanceof HTMLImageElement) {
    return clip(target.currentSrc || target.src || undefined);
  }

  if (target instanceof HTMLScriptElement) {
    return clip(target.src || undefined);
  }

  if (target instanceof HTMLLinkElement) {
    return clip(target.href || undefined);
  }

  return undefined;
}

function reportRuntimeError(
  kind: RuntimeErrorKind,
  error: unknown,
  extra: Partial<Omit<RuntimeErrorPayload, 'kind' | 'message' | 'timestamp' | 'url'>> = {},
): void {
  const normalized = normalizeError(error);
  emitRuntimeError(error, {
    kind,
    ...normalized,
    ...extra,
    timestamp: new Date().toISOString(),
    url: resolvePageUrl(),
  });
}

function handleWindowError(event: ErrorEvent): void {
  const resourceSource = getResourceSource(event.target);
  if (!event.error && resourceSource) {
    reportRuntimeError('window', `Failed to load resource: ${resourceSource}`, {
      source: resourceSource,
    });
    return;
  }

  reportRuntimeError('window', event.error ?? event.message, {
    column: event.colno || undefined,
    line: event.lineno || undefined,
    source: clip(event.filename || undefined) ?? resourceSource,
  });
}

export function installGlobalErrorHandling(app: App): void {
  const previousErrorHandler = app.config.errorHandler;
  app.config.errorHandler = (error, instance, info) => {
    reportRuntimeError('vue', error, {
      component: getComponentName(instance),
      info: clip(info),
    });
    previousErrorHandler?.(error, instance, info);
  };

  if (typeof window === 'undefined' || window.__OVERPASS_GLOBAL_ERROR_HANDLERS_INSTALLED__) {
    return;
  }

  window.__OVERPASS_GLOBAL_ERROR_HANDLERS_INSTALLED__ = true;
  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', (event) => {
    reportRuntimeError('unhandledrejection', event.reason, {
      info: 'Unhandled promise rejection',
    });
  });
}

export function logBootstrapError(error: unknown): never {
  reportRuntimeError('bootstrap', error, {
    info: 'App bootstrap failed',
  });

  if (error instanceof Error) {
    throw error;
  }

  throw new Error(normalizeError(error).message);
}
